import React, { useState, useEffect, useRef } from 'react';
import SurveyCard from '../SurveyCard';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';

// Round colours: the button that starts a round is forest green, the one that
// ends it is bright red, and everything in between keeps the colour it had.
const FOREST_GREEN = '#228b22';
const BRIGHT_RED = '#ff1744';
const MID_RED = '#e94560';

// Each interruption keeps its own colour the whole way through: pick Change and
// the button that starts it is the same blue, a shade deeper. Ending one is the
// last action of that round, so it goes red like any other last action.
const ROYAL_BLUE = '#2f56d9';
const ROYAL_BLUE_DEEP = '#1c3697';
const ORANGE = '#d97706';
const ORANGE_DEEP = '#9a5200';
const ORANGE_BRIGHT = '#fb8c00';
const ORANGE_BRIGHT_DEEP = '#b35f00';

// The confirm button on the end-of-test review, which is not a timing button.
const CONFIRM_GREEN = '#00c853';

// The action button is 70% of the height it used to be (72px -> 50px, and the
// double-height one to match), which is what makes room for a survey question
// below it without pushing the timing buttons off the screen.
const ACTION_H = '50px';
const TALL_ACTION_H = '101px';
// Shuffle / Change / Other chips: 70% of the 116px they were.
const CHIP_H = '81px';
// Starting and ending an interruption: double the action button, since those
// screens hold nothing else.
const START_H = '100px';
// The option buttons keep the height they always had; the type grows into them.
const OPTION_H = '79px';

const CHIP = {
  padding: '14px 8px',
  minWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  border: '2px solid transparent',
  borderRadius: '12px',
  color: '#ffffff',
  fontSize: '36px',
  fontWeight: 'bold',
  cursor: 'pointer',
  textAlign: 'center',
  boxSizing: 'border-box',
  lineHeight: 1,
};

// Every action button is the same shape; only the colour changes.
const ACTION_SHAPE = {
  width: '100%',
  height: ACTION_H,
  padding: '0 10px',
  border: 'none',
  borderRadius: '12px',
  color: '#ffffff',
  fontWeight: 'bold',
  cursor: 'pointer',
  marginTop: '8px',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  whiteSpace: 'nowrap',
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1a1a2e',
    padding: '20px',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    marginTop: '10px',
  },
  roundBadge: {
    backgroundColor: '#e94560',
    borderRadius: '20px',
    padding: '6px 16px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  dealerName: {
    color: '#a0a0b0',
    fontSize: '14px',
  },
  formCard: {
    backgroundColor: '#16213e',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '16px',
    boxSizing: 'border-box',
    width: '100%',
  },
  formTitle: {
    color: '#a0a0b0',
    fontSize: '13px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    marginBottom: '16px',
    textAlign: 'center',
  },
  // 3x title for the Roulette process screens (Level of Action / Winning Outside / Inside).
  formTitleLarge: {
    fontSize: '39px',
    letterSpacing: '1px',
    lineHeight: 1.1,
    marginBottom: '20px',
  },
  gridContainer: {
    display: 'grid',
    gap: '12px',
    marginBottom: '16px',
  },
  gridButton: {
    height: OPTION_H,
    padding: '0 6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    backgroundColor: '#8B0000',
    border: '2px solid #8B0000',
    borderRadius: '12px',
    color: '#ffcccc',
    fontSize: '26px',
    fontWeight: 'bold',
    cursor: 'pointer',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  gridButtonSelected: {
    height: OPTION_H,
    padding: '0 6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    backgroundColor: '#8B0000',
    border: '2px solid #ff4444',
    borderRadius: '12px',
    color: '#ffffff',
    fontSize: '26px',
    fontWeight: 'bold',
    cursor: 'pointer',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  secondaryRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '12px',
    marginBottom: '16px',
  },
  shuffleButton: { ...CHIP, backgroundColor: ORANGE, borderColor: ORANGE },
  shuffleButtonSelected: { ...CHIP, backgroundColor: ORANGE, borderColor: '#ffffff' },
  changeButton: { ...CHIP, backgroundColor: ROYAL_BLUE, borderColor: ROYAL_BLUE },
  changeButtonSelected: { ...CHIP, backgroundColor: ROYAL_BLUE, borderColor: '#ffffff' },
  otherButton: { ...CHIP, backgroundColor: ORANGE_BRIGHT, borderColor: ORANGE_BRIGHT },
  otherButtonSelected: { ...CHIP, backgroundColor: ORANGE_BRIGHT, borderColor: '#ffffff' },
  // Opening screen: 70% of the height they used to be, to leave room below.
  tallSecondaryButton: {
    height: CHIP_H,
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  timeButton: {
    width: '100%',
    height: ACTION_H,
    padding: '0 10px',
    backgroundColor: MID_RED,
    border: 'none',
    borderRadius: '12px',
    color: '#ffffff',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '8px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },
  // Roulette "Winning Outside / Inside" screens: options stacked vertically,
  // each the height of the standard action button (72px)…
  stackedVarButton: {
    height: '72px',
    paddingTop: '0px',
    paddingBottom: '0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // …and the action button doubled.
  tallTimeButton: {
    height: TALL_ACTION_H,
  },
  // Starting a shuffle, change or other pause keeps the colour of the button that
  // got you here, a shade deeper, and stays full height - it is the only thing on
  // that screen, so there is nothing to make room for.
  shuffleTimeButton: {
    ...ACTION_SHAPE,
    height: START_H,
    backgroundColor: ORANGE_DEEP,
  },
  changeTimeButton: {
    ...ACTION_SHAPE,
    height: START_H,
    backgroundColor: ROYAL_BLUE_DEEP,
  },
  otherTimeButton: {
    ...ACTION_SHAPE,
    height: START_H,
    backgroundColor: ORANGE_BRIGHT_DEEP,
  },
  // Ending one is the last action of that round, so it follows the same rule as
  // the last action of a dealing round.
  endSecondaryButton: {
    ...ACTION_SHAPE,
    height: START_H,
    backgroundColor: BRIGHT_RED,
  },
  disabledTimeButton: {
    width: '100%',
    height: ACTION_H,
    padding: '0 10px',
    backgroundColor: '#333',
    border: 'none',
    borderRadius: '12px',
    color: '#666',
    fontSize: '17px',
    fontWeight: 'bold',
    marginTop: '8px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    lineHeight: 1.15,
  },
  // Action button shown but dimmed until its variable is selected (brightens when active).
  dimmedTimeButton: {
    opacity: 0.4,
    cursor: 'default',
  },
  // End-of-test review: the scores at rest, one line each.
  reviewRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: '#16213e',
    borderRadius: '12px',
    padding: '14px 16px',
    marginBottom: '8px',
  },
  reviewQuestion: {
    color: '#ffffff',
    fontSize: '17px',
    fontWeight: 'bold',
    lineHeight: 1.25,
    flex: 1,
  },
  reviewScore: {
    backgroundColor: '#ffcc00',
    color: '#1a1a2e',
    borderRadius: '10px',
    minWidth: '54px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: 'bold',
    flexShrink: 0,
  },
  reviewScoreBlank: {
    backgroundColor: '#0f3460',
    color: '#8b93a7',
    borderRadius: '10px',
    minWidth: '54px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 'bold',
    flexShrink: 0,
  },
  reviewOutOf: {
    color: '#a0a0b0',
    fontSize: '13px',
    marginTop: '2px',
  },
  editButton: {
    width: '100%',
    height: '50px',
    backgroundColor: 'transparent',
    border: '2px solid #ffcc00',
    borderRadius: '12px',
    color: '#ffcc00',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '12px',
    boxSizing: 'border-box',
  },
  saveButton: {
    width: '100%',
    height: '50px',
    backgroundColor: CONFIRM_GREEN,
    border: 'none',
    borderRadius: '12px',
    color: '#04310f',
    fontSize: '20px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '12px',
    boxSizing: 'border-box',
  },
  cancelButton: {
    width: '100%',
    padding: '18px',
    backgroundColor: 'transparent',
    border: '2px solid #0f3460',
    borderRadius: '12px',
    color: '#a0a0b0',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '12px',
    boxSizing: 'border-box',
    display: 'block',
  },
  // Between rounds, ending is the tidy moment to do it, so the button is as wide
  // as the action button and lit up to say so.
  endSessionWide: {
    width: '100%',
    height: ACTION_H,
    // Sits apart from the round's own buttons, so it is not tapped by reflex.
    marginTop: '34px',
    backgroundColor: '#fff4bf',
    border: '3px solid #e8b100',
    borderRadius: '12px',
    color: '#3a2f00',
    fontSize: '22px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxSizing: 'border-box',
    display: 'block',
  },
  warnLine: {
    color: '#ffcc00',
    fontSize: '15px',
    lineHeight: 1.45,
    marginBottom: '10px',
  },
  plainLine: {
    color: '#a0a0b0',
    fontSize: '15px',
    lineHeight: 1.45,
    marginBottom: '10px',
  },
  backButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#a0a0b0',
    fontSize: '14px',
    cursor: 'pointer',
  },
  modal: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: 1000,
  },
  modalCard: {
    backgroundColor: '#16213e',
    borderRadius: '20px',
    padding: '32px 24px',
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center',
  },
  modalTitle: {
    color: '#e94560',
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '12px',
  },
  modalSubtitle: {
    color: '#a0a0b0',
    fontSize: '16px',
    marginBottom: '32px',
  },
  modalButton: {
    width: '100%',
    padding: '18px',
    backgroundColor: '#e94560',
    border: 'none',
    borderRadius: '12px',
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginBottom: '12px',
  },
  modalButtonSecondary: {
    width: '100%',
    padding: '18px',
    backgroundColor: 'transparent',
    border: '2px solid #0f3460',
    borderRadius: '12px',
    color: '#a0a0b0',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  endScreenCard: {
    backgroundColor: '#16213e',
    borderRadius: '16px',
    padding: '32px 24px',
    marginBottom: '16px',
    textAlign: 'center',
  },
  endScreenTitle: {
    color: '#a0a0b0',
    fontSize: '13px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    marginBottom: '24px',
  },
};

// Labels are sized to the width they really have. The width of the words is
// measured in the phone's own button font (a guess from the number of letters
// ran wide on narrow phones and pushed the text out of the button).
let measureCtx = null;
let buttonFamily = 'sans-serif';
function widthPerPx(text) {
  try {
    if (!measureCtx) {
      measureCtx = document.createElement('canvas').getContext('2d');
      const b = document.createElement('button');
      document.body.appendChild(b);
      buttonFamily = getComputedStyle(b).fontFamily || 'sans-serif';
      b.remove();
    }
    measureCtx.font = `bold 100px ${buttonFamily}`;
    return measureCtx.measureText(text).width / 100;
  } catch (e) {
    return text.length * 0.64;
  }
}
function fitFont(label, room, ceiling, floor) {
  const text = String(label || '');
  const fits = (room * 0.94) / Math.max(widthPerPx(text), 0.01);
  return `${Math.max(floor, Math.min(ceiling, Math.floor(fits)))}px`;
}
// The inside of the card: the screen less the page and card padding (20 + 24 each side).
function cardWidth() {
  return Math.min(typeof window !== 'undefined' ? window.innerWidth : 390, 520) - 88;
}

// The button lost a third of its height, so the label grows into the width it
// has instead, stepping down only far enough to keep a long label like
// "Players Have Acted" on one line inside the button (less its 10px padding).
function actionFont(label, tall) {
  return fitFont(label, cardWidth() - 20, tall ? 52 : 44, 16);
}

// The option buttons (spots, bet complexity, pay outcome) size the same way the
// action button does: as large as the longest label allows in the column it has.
function optionFont(label, cols) {
  const n = Math.max(cols, 1);
  return fitFont(label, (cardWidth() - (n - 1) * 12) / n - 16, 44, 14);
}

// Shuffle / Change / Other sit two to a row, all at the size the longest one allows.
function chipFont(labels, cols) {
  const n = Math.max(cols, 1);
  const sizes = labels.map(l => parseInt(fitFont(l, (cardWidth() - (n - 1) * 12) / n - 20, 36, 14), 10));
  return `${Math.min(...sizes)}px`;
}

// Forest green to start a round, bright red to end it, unchanged in between.
function actionColor(step, lastStep) {
  if (step === 0) return FOREST_GREEN;
  if (step >= lastStep) return BRIGHT_RED;
  return MID_RED;
}

// minmax(0, 1fr): columns stay equal and inside the card whatever the label
// (a plain 1fr column grows to fit its text and pushes the row past the edge).
function getGridCols(count) {
  if (count === 2) return 'minmax(0, 1fr) minmax(0, 1fr)';
  return 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';
}

// Secondary actions (Shuffle / Change / Other) styling helpers.
function secondaryTimeStyle(mode, ending) {
  if (ending) return styles.endSecondaryButton;
  if (mode === 'Shuffle') return styles.shuffleTimeButton;
  if (mode === 'Other') return styles.otherTimeButton;
  return styles.changeTimeButton;
}
function secondaryBadgeColor(mode) {
  if (mode === 'Shuffle') return ORANGE;
  if (mode === 'Other') return ORANGE_BRIGHT;
  return ROYAL_BLUE;
}
function secondaryButtonStyle(opt, selected, tall) {
  let base;
  if (opt === 'Shuffle') base = selected ? styles.shuffleButtonSelected : styles.shuffleButton;
  else if (opt === 'Other') base = selected ? styles.otherButtonSelected : styles.otherButton;
  else base = selected ? styles.changeButtonSelected : styles.changeButton;
  return tall ? { ...base, ...styles.tallSecondaryButton } : base;
}

function Survey() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const dealer = JSON.parse(localStorage.getItem('dealer') || '{}');
  const gameType = JSON.parse(localStorage.getItem('game_type') || '{}');
  const sessionId = localStorage.getItem('session_id');
  const targetRounds = parseInt(localStorage.getItem('target_rounds') || '0');

  const forms = gameType?.form_config?.forms || [];
  // What this game says a test needs, so the confirmation can be specific.
  const minScoringRounds = gameType?.min_scoring_rounds || 0;
  const benchmarkMinRounds = gameType?.benchmark_mode ? (gameType?.min_benchmark_rounds || 0) : 0;
  const secondaryOptions = gameType?.form_config?.forms?.[0]?.secondary_options || [];
  // When set, the action button is always shown (dimmed) and brightens when ready.
  const showActionAlways = !!gameType?.form_config?.always_show_action;

  const [step, setStep] = useState(0);
  const [selections, setSelections] = useState({});
  const [timestamps, setTimestamps] = useState({});
  const [roundNumber, setRoundNumber] = useState(
    parseInt(localStorage.getItem('round_number') || '1')
  );
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [secondaryMode, setSecondaryMode] = useState(null);
  const [secondaryStarted, setSecondaryStarted] = useState(false);
  const [secondaryTs1, setSecondaryTs1] = useState(null);

  // Custom survey questions: shown below the timing buttons once their round and
  // point in the process are reached. They never block or change the timing.
  const [surveyQs, setSurveyQs] = useState([]);
  const [surveyAnswers, setSurveyAnswers] = useState({});
  const [surveySkipped, setSurveySkipped] = useState({});
  const [reviewing, setReviewing] = useState(false);
  const [editingScores, setEditingScores] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [confirmingEnd, setConfirmingEnd] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    fetch(`${API}/sessions/${sessionId}/survey`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        setSurveyQs((d.questions || []).filter(q => q.active));
        setSurveyAnswers(d.answers || {});
      })
      .catch(() => {});
  }, [sessionId, token]);

  const dueQuestion = surveyQs.find(q =>
    !q.ask_at_end && !surveyAnswers[q.id] && !surveySkipped[q.id] &&
    (roundNumber > q.after_round + 1 || (roundNumber === q.after_round + 1 && step >= q.trigger_step))
  );

  async function answerSurvey(q, score) {
    setSurveyAnswers(prev => ({ ...prev, [q.id]: score }));
    try {
      const res = await fetch(`${API}/sessions/${sessionId}/survey-answers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ question_id: q.id, score, round_number: roundNumber }),
      });
      if (!res.ok) throw new Error('save failed');
    } catch (e) {
      setSurveyAnswers(prev => {
        const next = { ...prev };
        delete next[q.id];
        return next;
      });
      alert('Could not save the survey answer. Please try again.');
    }
  }

  function selectOption(key, value) {
    setSelections(prev => ({ ...prev, [key]: value }));
    setSecondaryMode(null);
  }

  function selectSecondary(mode) {
    setSecondaryMode(mode);
    setSelections({});
  }

  // Guards the last tap of a round and the end of a secondary round. State is
  // too slow here: two taps can both run before React re-renders, so the flag
  // has to be set synchronously.
  const sendingRef = useRef(false);

  function recordTimestamp(tsKey) {
    if (sendingRef.current) return;
    const now = new Date().toISOString();
    const newTimestamps = { ...timestamps, [tsKey]: now };
    if (step < forms.length - 1) {
      setTimestamps(newTimestamps);
      setStep(step + 1);
    } else {
      sendingRef.current = true;
      submitRound(newTimestamps);
    }
  }

  function handleSecondaryStart() {
    const now = new Date().toISOString();
    setSecondaryTs1(now);
    setSecondaryStarted(true);
  }

  async function handleSecondaryEnd() {
    if (sendingRef.current) return;
    sendingRef.current = true;
    const ts2 = new Date().toISOString();
    const mode = secondaryMode;
    // Clear the screen first: waiting on the save is what invited the second tap.
    setSecondaryMode(null);
    setSecondaryStarted(false);
    setSecondaryTs1(null);
    try {
      await fetch(`${API}/sessions/${sessionId}/rounds`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          round_number: roundNumber,
          round_type: mode.toLowerCase(),
          ts1: secondaryTs1,
          ts2,
        }),
      });
    } catch (e) {
      alert('Error saving');
    }
    sendingRef.current = false;
  }

  function cancelSecondary() {
    setSecondaryMode(null);
    setSecondaryStarted(false);
    setSecondaryTs1(null);
  }

  async function submitRound(finalTimestamps) {
    const nextRound = roundNumber + 1;
    localStorage.setItem('round_number', nextRound.toString());
    setRoundNumber(nextRound);
    setStep(0);
    setSelections({});
    setTimestamps({});
    setSecondaryMode(null);
    if (targetRounds > 0 && nextRound - 1 >= targetRounds) {
      setShowTargetModal(true);
    }
    try {
      await fetch(`${API}/sessions/${sessionId}/rounds`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          round_number: roundNumber,
          round_type: 'deal',
          v1: selections['v1'] || null,
          v2: selections['v2'] || null,
          v3: selections['v3'] || null,
          v4: selections['v4'] || null,
          ts1: finalTimestamps['ts1'] || null,
          ts2: finalTimestamps['ts2'] || null,
          ts3: finalTimestamps['ts3'] || null,
          ts4: finalTimestamps['ts4'] || null,
          ts5: finalTimestamps['ts5'] || null,
        }),
      });
    } catch (e) {
      alert('Error saving round');
    }
    sendingRef.current = false;
  }

  // A round is only sent to the server once its last button is tapped, so a round
  // part-way through has never left the phone. Ending here drops it, which is the
  // right behaviour - the observer just has to be told, rather than find out later.
  const roundInProgress = step > 0 || Object.keys(timestamps).length > 0;
  const roundsDone = roundNumber - 1;

  function endSession() {
    setConfirmingEnd(true);
  }

  function confirmEnd() {
    setConfirmingEnd(false);
    // With survey questions, the observer reviews and confirms the scores first;
    // nothing is final until they do. Without any, the test just ends.
    if (surveyQs.length > 0) {
      setReviewing(true);
      return;
    }
    finishSession();
  }

  async function finishSession() {
    setFinishing(true);
    try {
      await fetch(`${API}/sessions/${sessionId}/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate('/session-end');
    } catch (e) {
      setFinishing(false);
      alert('Could not end the test. Please try again.');
    }
  }

  function goBack() {
    if (step > 0) {
      setStep(step - 1);
      const prevTs = `ts${step}`;
      setTimestamps(prev => {
        const updated = { ...prev };
        delete updated[prevTs];
        return updated;
      });
    }
  }

  if (reviewing) {
    const unanswered = surveyQs.filter(q => !surveyAnswers[q.id]).length;
    return (
      <div style={styles.container}>
        <div style={styles.topBar}>
          <div style={styles.dealerName}>
            {dealer.first_name} {dealer.last_name}
          </div>
          <div style={styles.roundBadge}>{roundNumber - 1} rounds</div>
        </div>

        <div style={styles.formCard}>
          <div style={styles.formTitle}>Behavior scores</div>
          <div style={{ color: '#a0a0b0', fontSize: '14px', lineHeight: 1.5 }}>
            {editingScores
              ? 'Tap a score to change it, then go back to the list.'
              : 'Score any question still open, check the rest, then save the test. After that they cannot be changed here.'}
          </div>
        </div>

        {editingScores ? (
          surveyQs.map(q => (
            <SurveyCard
              key={q.id}
              question={q}
              selected={surveyAnswers[q.id] || null}
              onScore={score => answerSurvey(q, score)}
            />
          ))
        ) : (
          surveyQs.map(q => (!surveyAnswers[q.id] ? (
            // Not scored yet (or asked only at the end): score it right here
            <SurveyCard
              key={q.id}
              question={q}
              selected={null}
              onScore={score => answerSurvey(q, score)}
            />
          ) : (
            <div key={q.id} style={styles.reviewRow}>
              <div>
                <div style={styles.reviewQuestion}>{q.question_text}</div>
                <div style={styles.reviewOutOf}>out of {q.scale_max}</div>
              </div>
              <div style={surveyAnswers[q.id] ? styles.reviewScore : styles.reviewScoreBlank}>
                {surveyAnswers[q.id] || '—'}
              </div>
            </div>
          )))
        )}

        {unanswered > 0 && (
          <div style={{ color: '#ffcc00', fontSize: '13px', textAlign: 'center', marginTop: '10px' }}>
            {unanswered} question(s) not scored. You can still save; unscored questions are left blank.
          </div>
        )}

        {editingScores ? (
          <button style={styles.saveButton} onClick={() => setEditingScores(false)}>
            Done editing
          </button>
        ) : (
          <>
            <button style={styles.saveButton} onClick={finishSession} disabled={finishing}>
              {finishing ? 'Saving...' : 'Save & Finish'}
            </button>
            <button style={styles.editButton} onClick={() => setEditingScores(true)} disabled={finishing}>
              Edit scores
            </button>
          </>
        )}

        <button
          style={{ ...styles.backButton, width: '100%', marginTop: '12px' }}
          onClick={() => setReviewing(false)}
          disabled={finishing}
        >
          ← Back to the test
        </button>
      </div>
    );
  }

  if (secondaryStarted) {
    return (
      <div style={styles.container}>
        <div style={styles.topBar}>
          <div style={styles.dealerName}>
            {dealer.first_name} {dealer.last_name}
          </div>
          <div style={{
            ...styles.roundBadge,
            backgroundColor: secondaryBadgeColor(secondaryMode)
          }}>
            {secondaryMode}
          </div>
        </div>
        <div style={styles.endScreenCard}>
          <div style={styles.endScreenTitle}>
            {secondaryMode} in progress
          </div>
          <button
            style={{
              ...secondaryTimeStyle(secondaryMode, true),
              fontSize: actionFont(`End ${secondaryMode}`, false),
            }}
            onClick={handleSecondaryEnd}
          >
            End {secondaryMode}
          </button>
          <button style={styles.cancelButton} onClick={cancelSecondary}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  const currentForm = forms[step];
  const formVars = currentForm
    ? (Array.isArray(currentForm.variables) && currentForm.variables.length > 0
        ? currentForm.variables
        : currentForm.variable_key
          ? [{ key: currentForm.variable_key, label: currentForm.label, options: currentForm.options || [] }]
          : [])
    : [];
  const stageReveal = !!currentForm?.stage_reveal;
  const firstUnselectedIdx = formVars.findIndex(v => !selections[v.key]);
  const visibleCount = !stageReveal
    ? formVars.length
    : (firstUnselectedIdx === -1 ? formVars.length : firstUnselectedIdx + 1);
  const visibleVars = formVars.slice(0, visibleCount);
  // A form with no variables (e.g. Roulette "Start Spin" on screen 1, where
  // # players is captured on the next screen) is ready as soon as it renders.
  const allSelected = formVars.length === 0 || formVars.every(v => selections[v.key]);
  const tsKey = `ts${step + 1}`;
  const isForm1 = step === 0;
  // Per-screen layout: "stacked" = options stacked full-width + tall action button.
  const stacked = currentForm?.button_layout === 'stacked';
  // Double-height action button (implied by stacked, or set explicitly via tall_action).
  const tallAction = stacked || !!currentForm?.tall_action;

  return (
    <div style={styles.container}>
      {confirmingEnd && (
        <div style={styles.modal}>
          <div style={styles.modalCard}>
            <div style={styles.modalTitle}>End this test?</div>
            <div style={styles.plainLine}>
              {roundsDone === 0
                ? 'No rounds have been recorded yet. Ending now records nothing at all.'
                : `${roundsDone} round${roundsDone === 1 ? '' : 's'} recorded and saved.`}
            </div>
            {roundInProgress && (
              <div style={styles.warnLine}>
                Round {roundNumber} is part-way through. It will not be recorded.
              </div>
            )}
            {roundsDone > 0 && minScoringRounds > 0 && roundsDone < minScoringRounds && (
              <div style={styles.warnLine}>
                A test needs {minScoringRounds} rounds to produce a score.
              </div>
            )}
            {roundsDone > 0 && benchmarkMinRounds > 0 && roundsDone < benchmarkMinRounds && (
              <div style={styles.warnLine}>
                It needs {benchmarkMinRounds} rounds to count toward the benchmark.
              </div>
            )}
            <button style={styles.modalButton} onClick={confirmEnd}>
              End Test
            </button>
            <button style={styles.modalButtonSecondary} onClick={() => setConfirmingEnd(false)}>
              Keep Going
            </button>
          </div>
        </div>
      )}

      {showTargetModal && (
        <div style={styles.modal}>
          <div style={styles.modalCard}>
            <div style={styles.modalTitle}>{targetRounds} Rounds!</div>
            <div style={styles.modalSubtitle}>
              You've reached your target. What would you like to do?
            </div>
            <button style={styles.modalButton} onClick={endSession}>
              End Session
            </button>
            <button
              style={styles.modalButtonSecondary}
              onClick={() => setShowTargetModal(false)}
            >
              Keep Going
            </button>
          </div>
        </div>
      )}

      <div style={styles.topBar}>
        <div style={styles.dealerName}>
          {dealer.first_name} {dealer.last_name}
        </div>
        <div style={styles.roundBadge}>Round {roundNumber}</div>
      </div>

      {currentForm && (
        <div style={styles.formCard}>
          {currentForm.label && (
            <div style={currentForm.title_large ? { ...styles.formTitle, ...styles.formTitleLarge } : styles.formTitle}>{currentForm.label}</div>
          )}

          {visibleVars.map((vdef) => {
            const optionCols = (vdef.options || []).length === 2 ? 2 : 3;
            return (
            <React.Fragment key={vdef.key}>
              {formVars.length > 1 && vdef.label && (
                <div style={currentForm.title_large ? { ...styles.formTitle, ...styles.formTitleLarge } : styles.formTitle}>{vdef.label}</div>
              )}
              <div style={{
                ...styles.gridContainer,
                gridTemplateColumns: stacked ? 'minmax(0, 1fr)' : getGridCols((vdef.options || []).length),
              }}>
                {(vdef.options || []).map(option => (
                  <button
                    key={option}
                    style={{
                      ...(selections[vdef.key] === option
                        ? styles.gridButtonSelected
                        : styles.gridButton),
                      ...(stacked ? styles.stackedVarButton : {}),
                      fontSize: optionFont(option, stacked ? 1 : optionCols),
                    }}
                    onClick={() => selectOption(vdef.key, option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </React.Fragment>
            );
          })}

          {isForm1 && secondaryOptions.length > 0 && (
            <div style={styles.secondaryRow}>
              {secondaryOptions.map(opt => (
                <button
                  key={opt}
                  style={{
                    ...secondaryButtonStyle(opt, secondaryMode === opt, tallAction),
                    fontSize: chipFont(secondaryOptions, 2),
                  }}
                  onClick={() => selectSecondary(opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          )}

          {secondaryMode ? (
            <button
              style={{
                ...secondaryTimeStyle(secondaryMode, false),
                fontSize: actionFont(`Start ${secondaryMode}`, false),
              }}
              onClick={handleSecondaryStart}
            >
              Start {secondaryMode}
            </button>
          ) : allSelected ? (
            <button
              style={{
                ...styles.timeButton,
                ...(tallAction ? styles.tallTimeButton : {}),
                backgroundColor: actionColor(step, forms.length - 1),
                fontSize: actionFont(currentForm.time_button, tallAction),
              }}
              onClick={() => recordTimestamp(tsKey)}
            >
              {currentForm.time_button}
            </button>
          ) : showActionAlways && currentForm?.time_button ? (
            <button
              disabled
              style={{
                ...styles.timeButton,
                ...(tallAction ? styles.tallTimeButton : {}),
                backgroundColor: actionColor(step, forms.length - 1),
                fontSize: actionFont(currentForm.time_button, tallAction),
                ...styles.dimmedTimeButton,
              }}
            >
              {currentForm.time_button}
            </button>
          ) : (
            <button disabled style={styles.disabledTimeButton}>
              {isForm1 ? 'Select to begin or tap Shuffle / Change' : 'Select an option'}
            </button>
          )}

          {!roundInProgress && !secondaryMode && (
            <button style={styles.endSessionWide} onClick={endSession}>
              End Session
            </button>
          )}
        </div>
      )}

      {dueQuestion && (
        <SurveyCard
          question={dueQuestion}
          selected={null}
          onScore={score => answerSurvey(dueQuestion, score)}
          onSkip={() => setSurveySkipped(prev => ({ ...prev, [dueQuestion.id]: true }))}
        />
      )}

      {(roundInProgress || secondaryMode) && (
        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          {step > 0 && (
            <button style={styles.backButton} onClick={goBack}>
              ← Back
            </button>
          )}
          <button
            style={{ ...styles.backButton, marginLeft: 'auto' }}
            onClick={endSession}
          >
            End Session
          </button>
        </div>
      )}
    </div>
  );
}

export default Survey;